import React, { useEffect, useRef, useState } from 'react';
import { getAuthToken } from '../utils/authUtils';
import { bottomSheet, useBottomSheet } from './BottomSheet';
import { useCountUp } from './useCountUp';
import './RedeemCouponSheet.css';

const e = React.createElement;

const ENTER = 'enter';
const SENDING = 'sending';
const DONE = 'done';

const GENERIC_ERROR = 'Invalid coupon code.';

const formatCoins = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-IN') : n;

/**
 * Bottom sheet that turns a coins coupon into EG Coins.
 *
 * Three stages in one surface rather than three screens: the code goes in, the
 * button reports the wait, and the same panel becomes the receipt. Someone
 * redeeming a code wants to watch the coins land, so the sheet does not close
 * itself on success -- it shows what arrived and what the balance is now, and
 * leaves closing to them.
 *
 * `onRedeemed` fires as the credit lands rather than on close, so the wallet
 * behind the sheet is already correct by the time it slides away.
 *
 * `initialCode` fills the field when the sheet opens, for a coupon link
 * that brought the code with it; the customer only has to tap Redeem.
 */
export const RedeemCouponSheet = ({
  open, onClose, onRedeemed, getAPIUrl, initialCode = '',
}) => {
  const [stage, setStage] = useState(ENTER);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  // Never dismissed mid-request: the credit is already on its way, and
  // closing here would leave the customer not knowing whether it landed.
  const { closing, requestClose } = useBottomSheet({
    open,
    onClose,
    blocked: stage === SENDING,
  });

  // Seeded at 0 rather than undefined: the hook skips its first value, so the
  // receipt would otherwise land on the total without counting to it.
  const coinsShown = useCountUp(stage === DONE ? result?.coins : 0);

  // Every open starts clean: a code that failed last time must not still be
  // in the field, and last time's receipt must not flash up before the form.
  // A code that came in on a coupon link is the one exception.
  useEffect(() => {
    if (!open) return undefined;
    setStage(ENTER);
    setCode(initialCode);
    setError(null);
    setShake(false);
    setResult(null);
    // Focused once the slide-up is under way. On the first frame a mobile
    // keyboard races the animation and the sheet judders.
    const timer = setTimeout(() => inputRef.current?.focus(), 280);
    return () => clearTimeout(timer);
  }, [open, initialCode]);

  const submit = async (evt) => {
    evt.preventDefault();
    const value = code.trim();
    if (!value || stage === SENDING) return;
    setStage(SENDING);
    setError(null);
    try {
      const res = await fetch(
        getAPIUrl('/api/payment/v2/wallet/coupon/redeem/'),
        {
          method: 'POST',
          headers: {
            Authorization: getAuthToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ coupon_code: value }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStage(DONE);
        if (onRedeemed) onRedeemed(data);
        return;
      }
      setError(data.message || GENERIC_ERROR);
      setShake(true);
      setStage(ENTER);
    } catch (err) {
      setError('Could not reach the server. Please try again.');
      setShake(true);
      setStage(ENTER);
    }
  };

  if (!open) return null;

  const enterPanel = e(
    'form',
    { className: 'rcs-body', onSubmit: submit },
    e(
      'span',
      { className: 'rcs-ticket-icon', role: 'img', 'aria-label': 'Coupon' },
      '🎟️'
    ),
    e('h2', { className: 'rcs-title', id: 'rcs-title' }, 'Redeem a Coupon'),
    e(
      'p',
      { className: 'rcs-sub' },
      'Enter your code and the coins go straight into your wallet.'
    ),
    e(
      'div',
      {
        className: `rcs-stub${shake ? ' shake' : ''}`,
        onAnimationEnd: () => setShake(false),
      },
      e('input', {
        ref: inputRef,
        className: 'rcs-input',
        type: 'text',
        value: code,
        placeholder: 'COUPON CODE',
        onChange: (evt) => {
          setCode(evt.target.value);
          if (error) setError(null);
        },
        disabled: stage === SENDING,
        autoComplete: 'off',
        autoCapitalize: 'characters',
        spellCheck: false,
        'aria-label': 'Coupon code',
        'aria-invalid': !!error,
        'aria-describedby': error ? 'rcs-error' : undefined,
      })
    ),
    error
      ? e(
          'p',
          { className: 'rcs-error', id: 'rcs-error', role: 'alert' },
          e('span', { 'aria-hidden': 'true' }, '⚠  '),
          error
        )
      : null,
    e(
      'button',
      {
        className: 'rcs-primary',
        type: 'submit',
        disabled: stage === SENDING || !code.trim(),
      },
      stage === SENDING
        ? e(
            React.Fragment,
            null,
            e('span', { className: 'rcs-spinner', 'aria-hidden': 'true' }),
            'Redeeming...'
          )
        : 'Redeem'
    ),
    e(
      'button',
      {
        className: 'rcs-ghost',
        type: 'button',
        onClick: requestClose,
        disabled: stage === SENDING,
      },
      'Cancel'
    )
  );

  const donePanel = e(
    'div',
    { className: 'rcs-body' },
    e(
      'div',
      { className: 'rcs-burst' },
      e('span', { className: 'rcs-ring one' }),
      e('span', { className: 'rcs-ring two' }),
      e('div', { className: 'rcs-coin' }, 'EG')
    ),
    e(
      'p',
      { className: 'rcs-won', id: 'rcs-title' },
      '+',
      formatCoins(coinsShown),
      e('span', { className: 'rcs-won-unit' }, ' EG Coins')
    ),
    e('p', { className: 'rcs-sub' }, 'Added to your wallet.'),
    e(
      'dl',
      { className: 'rcs-receipt' },
      e('dt', null, 'New balance'),
      e('dd', null, `${formatCoins(result?.balance)} EG Coins`),
      result?.expires_at
        ? e(
            React.Fragment,
            null,
            e('dt', null, 'Valid till'),
            e('dd', null, result.expires_at)
          )
        : null
    ),
    e(
      'button',
      { className: 'rcs-primary', type: 'button', onClick: requestClose },
      'Done'
    )
  );

  return bottomSheet({
    closing,
    requestClose,
    labelledBy: 'rcs-title',
    panel: stage === DONE ? donePanel : enterPanel,
  });
};

export default RedeemCouponSheet;
